import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Input, InputNumber, Select, Switch, message, Spin, Tooltip } from "antd";
import {
  SaveOutlined, ArrowLeftOutlined, PlusOutlined, SearchOutlined,
} from "@ant-design/icons";
import api from "../../../../../core/axios/axiosInstance";
import { updateFlow, createFlow } from "../../../../../core/data/redux/slices/WabaAutomationSlice";
import { fetchTags } from "../../../../../core/data/redux/slices/TagSlice";

import TriggerNode           from "./nodes/TriggerNode";
import TextNode              from "./nodes/TextNode";
import MediaNode             from "./nodes/MediaNode";
import InteractiveButtonsNode from "./nodes/InteractiveButtonsNode";
import DelayNode             from "./nodes/DelayNode";
import ActionNode            from "./nodes/ActionNode";
import InteractiveListNode   from "./nodes/InteractiveListNode";
import SendEmailNode         from "./nodes/SendEmailNode";
import CaptureInputNode      from "./nodes/CaptureInputNode";
import QuestionnaireNode     from "./nodes/QuestionnaireNode";
import WaFlowNode            from "./nodes/WaFlowNode";
import JumpNode              from "./nodes/JumpNode";
import ConditionNode         from "./nodes/ConditionNode";
import SetVariableNode       from "./nodes/SetVariableNode";
import CsatNode              from "./nodes/CsatNode";
import LocationNode          from "./nodes/LocationNode";
import ABSplitNode           from "./nodes/ABSplitNode";
import DripNode              from "./nodes/DripNode";
import TemplateNode         from "./nodes/TemplateNode";
import ScheduleMeetingNode  from "./nodes/ScheduleMeetingNode";

const nodeTypes = {
  trigger:            TriggerNode,
  text:               TextNode,
  media:              MediaNode,
  interactiveButtons: InteractiveButtonsNode,
  interactiveList:    InteractiveListNode,
  delay:              DelayNode,
  addToGroup:         (p) => <ActionNode {...p} type="addToGroup" />,
  removeFromGroup:    (p) => <ActionNode {...p} type="removeFromGroup" />,
  updateContact:      (p) => <ActionNode {...p} type="updateContact" />,
  sendEmail:          SendEmailNode,
  captureInput:       CaptureInputNode,
  questionnaire:      QuestionnaireNode,
  waFlow:             WaFlowNode,
  webhook:            (p) => <ActionNode {...p} type="webhook" />,
  jump:               JumpNode,
  condition:          ConditionNode,
  setVariable:        SetVariableNode,
  csat:               CsatNode,
  locationRequest:    LocationNode,
  abSplit:            ABSplitNode,
  drip:               DripNode,
  template:           TemplateNode,
  scheduleMeeting:    ScheduleMeetingNode,
};

const NODE_PALETTE = {
  Trigger: [
    { type: "trigger", label: "Trigger", color: "#6366f1" },
  ],
  Messages: [
    { type: "text",               label: "Text Message",      color: "#10b981" },
    { type: "media",              label: "Media",             color: "#f59e0b" },
    { type: "interactiveButtons", label: "Buttons",           color: "#8b5cf6" },
    { type: "interactiveList",    label: "Interactive List",  color: "#06b6d4" },
    { type: "template",           label: "Template",          color: "#ec4899" },
  ],
  Actions: [
    { type: "delay",              label: "Delay",             color: "#06b6d4" },
    { type: "captureInput",       label: "Capture Input",     color: "#06b6d4" },
    { type: "questionnaire",      label: "Questionnaire",     color: "#8b5cf6" },
    { type: "waFlow",             label: "WhatsApp Flow",     color: "#7c3aed" },
    { type: "addToGroup",         label: "Add to Group",      color: "#10b981" },
    { type: "removeFromGroup",    label: "Remove from Group", color: "#ef4444" },
    { type: "updateContact",      label: "Update Contact",    color: "#f59e0b" },
    { type: "sendEmail",          label: "Send Email",        color: "#3b82f6" },
    { type: "scheduleMeeting",    label: "Schedule Meeting",  color: "#7c3aed" },
    { type: "webhook",            label: "Webhook",           color: "#8b5cf6" },
    { type: "drip",               label: "Drip Message",      color: "#3b82f6" },
    { type: "setVariable",        label: "Set Variable",      color: "#06b6d4" },
    { type: "csat",               label: "CSAT Rating",       color: "#f59e0b" },
    { type: "locationRequest",    label: "Request Location",  color: "#10b981" },
  ],
  Logic: [
    { type: "condition",          label: "Condition",         color: "#8b5cf6" },
    { type: "abSplit",            label: "A/B Split",         color: "#6366f1" },
    { type: "jump",               label: "Jump to Node",      color: "#f59e0b" },
  ],
};

let nodeId = 100;
const uid = () => `node_${++nodeId}_${Date.now()}`;

const FlowBuilderInner = () => {
  const { screenToFlowPosition, fitView } = useReactFlow();
  const canvasRef  = useRef(null);
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { id }     = useParams(); // undefined = new flow
  const flowBasePath = location.pathname.startsWith("/admin/")
    ? "/admin/whatsapp/automations"
    : "/whatsapp/automation/flows";

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [edgeLabelPopup, setEdgeLabelPopup] = useState(null); // { edgeId, x, y, value }
  const [flowName, setFlowName]          = useState("Untitled Flow");
  const [cancelKeywords, setCancelKeywords] = useState([]);
  const [cancelReply, setCancelReply]       = useState("");
  const [timeoutHours, setTimeoutHours]     = useState(24);
  const [timeoutMinutes, setTimeoutMinutes] = useState(0);
  const [timeoutReply, setTimeoutReply]     = useState("");
  const [loading, setLoading]            = useState(false);
  const [saving, setSaving]              = useState(false);
  const [isActive, setIsActive]          = useState(false);
  const [saveSummaryNote, setSaveSummaryNote] = useState(false);
  const [searchQuery, setSearchQuery]    = useState("");

  // Load groups for AddToGroup / RemoveFromGroup nodes
  useEffect(() => { dispatch(fetchTags()); }, []);

  // Load existing flow
  useEffect(() => {
    if (!id) {
      // New flow — add default trigger node
      setNodes([{
        id: "trigger_1",
        type: "trigger",
        position: { x: 80, y: 200 },
        data: { triggerType: "keyword", matchCriteria: "contains", keyword: "", onChange: makeOnChange("trigger_1") },
      }]);
      return;
    }
    setLoading(true);
    api.get(`/api/waba/automation/flows/${id}`)
      .then((res) => {
        const flow = res.data.data;
        setFlowName(flow.name);
        setCancelKeywords(flow.cancelKeyword ? flow.cancelKeyword.split(",").map(k => k.trim()).filter(Boolean) : []);
        setCancelReply(flow.cancelReply || "");
        setTimeoutHours(flow.timeoutHours ?? 24);
        setTimeoutMinutes(flow.timeoutMinutes ?? 0);
        setTimeoutReply(flow.timeoutReply || "");
        setIsActive(flow.isActive ?? false);
        setSaveSummaryNote(flow.saveSummaryNote ?? false);
        const savedNodes = flow.nodes || [];
        if (savedNodes.length === 0) {
          setNodes([{
            id: "trigger_1",
            type: "trigger",
            position: { x: 80, y: 200 },
            data: { triggerType: "keyword", matchCriteria: "contains", keyword: "", onChange: makeOnChange("trigger_1") },
          }]);
        } else {
          // Derive flowTriggerType from saved trigger node so all nodes get it on load
          const savedTriggerType = savedNodes.find(n => n.type === "trigger")?.data?.triggerType || "keyword";
          setNodes(savedNodes.map((n) => ({
            ...n,
            data: {
              ...n.data,
              flowTriggerType: savedTriggerType,
              onChange: makeOnChange(n.id),
              onDuplicate: () => duplicateNode(n.id),
            },
          })));
        }
        setEdges(flow.edges || []);
      })
      .catch(() => message.error("Failed to load flow"))
      .finally(() => setLoading(false));
  }, [id]);

  const makeOnChange = (nodeId) => (newData) => {
    setNodes((nds) => {
      // When trigger node changes triggerType, propagate flowTriggerType to all other nodes
      // so they can show context-aware UI (e.g. reg_* fields in ConditionNode).
      const updatedTriggerType = newData.type === "trigger" || nds.find(n => n.id === nodeId)?.type === "trigger"
        ? (newData.triggerType || "keyword")
        : null;
      return nds.map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...newData, onChange: makeOnChange(nodeId), onDuplicate: () => duplicateNode(nodeId) } };
        }
        if (updatedTriggerType !== null) {
          return { ...n, data: { ...n.data, flowTriggerType: updatedTriggerType } };
        }
        return n;
      });
    });
  };

  const duplicateNode = (nodeId) => {
    setNodes((nds) => {
      const src = nds.find((n) => n.id === nodeId);
      if (!src) return nds;
      const newId = uid();
      const { onChange, onDuplicate, ...cleanData } = src.data;
      const newNode = {
        ...src,
        id: newId,
        position: { x: src.position.x + 40, y: src.position.y + 40 },
        selected: true,
        data: { ...cleanData, onChange: makeOnChange(newId), onDuplicate: () => duplicateNode(newId) },
      };
      // Deselect source so new node wins the z-index battle
      return [...nds.map((n) => n.id === nodeId ? { ...n, selected: false } : n), newNode];
    });
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: "#6366f1" },
    }, eds));
  }, []);

  const onEdgeDoubleClick = useCallback((event, edge) => {
    event.preventDefault();
    setEdgeLabelPopup({ edgeId: edge.id, x: event.clientX, y: event.clientY, value: edge.label || "" });
  }, []);

  const commitEdgeLabel = (edgeId, label) => {
    setEdges((eds) => eds.map((e) => e.id === edgeId ? { ...e, label: label.trim() || undefined } : e));
    setEdgeLabelPopup(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("application/reactflow");
    if (!type) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    addNode(type, position);
  }, [screenToFlowPosition]);

  const addNode = (type, position) => {
    if (type === "trigger" && nodes.some((n) => n.type === "trigger")) {
      message.warning("Flow already has a Trigger node. Delete the existing one first.");
      return;
    }
    const newId = uid();
    const defaultData = {};
    if (type === "trigger") {
      defaultData.triggerType = "keyword";
      defaultData.matchCriteria = "contains";
      defaultData.keyword = "";
    }
    if (type === "interactiveButtons") {
      defaultData.buttons = [{ id: `btn_${Date.now()}`, label: "" }];
    }
    if (type === "interactiveList") {
      defaultData.sections = [
        {
          id: `sec_${Date.now()}`,
          title: "",
          rows: [{ id: `row_${Date.now() + 1}`, title: "", description: "" }],
        },
      ];
    }
    // Auto-generate node label: "Text Message 2", "Delay 1", etc.
    const paletteItem = Object.values(NODE_PALETTE).flat().find((item) => item.type === type);
    const label = paletteItem?.label || type;
    const sameTypeCount = nodes.filter((n) => n.type === type).length + 1;
    defaultData.nodeName = `${label} ${sameTypeCount}`;

    const currentTriggerType = nodes.find(n => n.type === "trigger")?.data?.triggerType || "keyword";
    const newNode = {
      id: newId,
      type,
      position: position ?? { x: Math.random() * 300 + 300, y: Math.random() * 300 + 100 },
      data: { ...defaultData, flowTriggerType: currentTriggerType, onChange: makeOnChange(newId), onDuplicate: () => duplicateNode(newId) },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanNodes = nodes.map(({ data, ...rest }) => {
        const { onChange, onDuplicate, uploading, fileName, ...cleanData } = data;
        return { ...rest, data: cleanData };
      });

      const payload = { name: flowName, nodes: cleanNodes, edges, isActive, saveSummaryNote, cancelKeyword: cancelKeywords.map(k => k.trim()).filter(Boolean).join(","), cancelReply: cancelReply.trim(), timeoutHours, timeoutMinutes, timeoutReply: timeoutReply.trim() };

      if (id) {
        await dispatch(updateFlow({ id, ...payload })).unwrap();
      } else {
        const result = await dispatch(createFlow(payload)).unwrap();
        navigate(`${flowBasePath}/${result._id}`, { replace: true });
      }
      message.success("Flow saved");
    } catch (err) {
      message.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-wrapper"><div className="content" style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spin /></div></div>;

  return (
    <div className="page-wrapper" style={{ paddingBottom: 0 }}>
      <div className="content" style={{ padding: 0, height: "calc(100vh - 60px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
        borderBottom: "1px solid #e5e7eb", background: "#fff",
      }}>
        <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => navigate(-1)}>Back</Button>
        <Input
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          style={{ maxWidth: 280, fontWeight: 600 }}
          size="small"
          bordered={false}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>Cancel keyword:</span>
          <Tooltip title="User sends any of these words to exit the flow. 'cancel', 'stop', 'exit' always work by default. Type a word and press Enter or comma to add.">
            <Select
              mode="tags"
              value={cancelKeywords}
              onChange={setCancelKeywords}
              tokenSeparators={[","]}
              style={{ minWidth: 160 }}
              size="small"
              placeholder="e.g. quit, stop"
              open={false}
            />
          </Tooltip>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>Cancel reply:</span>
          <Tooltip title="Message sent to user when they exit the flow via a cancel keyword. Leave blank for default message.">
            <Input
              value={cancelReply}
              onChange={(e) => setCancelReply(e.target.value)}
              placeholder="You have exited the flow..."
              size="small"
              style={{ minWidth: 220 }}
            />
          </Tooltip>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>Timeout:</span>
          <Tooltip title="If the user doesn't reply within this duration, the flow session expires.">
            <InputNumber value={timeoutHours} onChange={setTimeoutHours} min={0} size="small" style={{ width: 75 }} addonAfter="h" />
          </Tooltip>
          <InputNumber value={timeoutMinutes} onChange={setTimeoutMinutes} min={0} max={59} size="small" style={{ width: 75 }} addonAfter="m" />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>Timeout msg:</span>
          <Tooltip title="Message sent when session expires. Leave blank to silently end the session.">
            <Input
              value={timeoutReply}
              onChange={(e) => setTimeoutReply(e.target.value)}
              placeholder="Your session has expired..."
              size="small"
              style={{ minWidth: 200 }}
            />
          </Tooltip>
        </div>
        {/* Node Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8, position: "relative" }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            size="small"
            placeholder="Search nodes…"
            value={searchQuery}
            onChange={(e) => {
              const q = e.target.value;
              setSearchQuery(q);
              const lower = q.trim().toLowerCase();
              setNodes((nds) => nds.map((n) => {
                const matches = lower
                  ? (n.data?.nodeName || "").toLowerCase().includes(lower) || (n.type || "").toLowerCase().includes(lower)
                  : false;
                return { ...n, selected: false, className: matches ? "search-highlight" : "" };
              }));
            }}
            allowClear
            style={{ width: searchQuery ? 200 : 140, transition: "width 0.2s" }}
          />
          {searchQuery.trim() && (() => {
            const q = searchQuery.trim().toLowerCase();
            const matches = nodes.filter((n) =>
              (n.data?.nodeName || "").toLowerCase().includes(q) || (n.type || "").toLowerCase().includes(q)
            );
            return matches.length > 0 ? (
              <div style={{
                position: "absolute", top: 30, left: 0, zIndex: 1000,
                background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                border: "1px solid #e5e7eb", minWidth: 200, maxHeight: 260, overflowY: "auto",
                padding: "4px 0",
              }}>
                {matches.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => {
                      fitView({ nodes: [{ id: n.id }], duration: 400, padding: 0.5 });
                      setSearchQuery("");
                      setNodes((nds) => nds.map((nd) => ({ ...nd, className: "" })));
                    }}
                    style={{ padding: "7px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #f3f4f6" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fef3c7"}
                    onMouseLeave={(e) => e.currentTarget.style.background = ""}
                  >
                    <div style={{ fontWeight: 600, color: "#92400e" }}>{n.data?.nodeName || n.type}</div>
                    {n.data?.nodeName && <div style={{ fontSize: 10, color: "#9ca3af" }}>{n.type}</div>}
                  </div>
                ))}
              </div>
            ) : null;
          })()}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <Tooltip title="When ON — saves a single summary note on the contact at session end.">
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>Summary note</span>
              <Switch
                size="small"
                checked={saveSummaryNote}
                onChange={setSaveSummaryNote}
              />
            </div>
          </Tooltip>
          <Tooltip title={isActive ? "Flow is live — triggers will fire" : "Flow is inactive — triggers won't fire"}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Switch
                size="small"
                checked={isActive}
                onChange={setIsActive}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </div>
          </Tooltip>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            Save Flow
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex" }}>
        {/* Node Palette */}
        <div style={{
          width: 180, borderRight: "1px solid #e5e7eb", background: "#f9fafb",
          padding: 12, overflowY: "auto",
        }}>
          {Object.entries(NODE_PALETTE).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {section}
              </div>
              {items.map((item) => (
                <div
                  key={item.type}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/reactflow", item.type);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={() => addNode(item.type)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 10px", borderRadius: 7, marginBottom: 5,
                    border: `1px solid ${item.color}22`,
                    background: `${item.color}11`,
                    cursor: "grab", fontSize: 12, fontWeight: 500,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = `${item.color}22`}
                  onMouseLeave={(e) => e.currentTarget.style.background = `${item.color}11`}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Edge label popup */}
        {edgeLabelPopup && (
          <div
            style={{
              position: "fixed",
              top: edgeLabelPopup.y - 20,
              left: edgeLabelPopup.x - 100,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #6366f1",
              borderRadius: 8,
              padding: "8px 10px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              display: "flex",
              gap: 6,
              alignItems: "center",
            }}
          >
            <Input
              autoFocus
              size="small"
              placeholder="Edge label (optional)"
              defaultValue={edgeLabelPopup.value}
              style={{ width: 180 }}
              onPressEnter={(e) => commitEdgeLabel(edgeLabelPopup.edgeId, e.target.value)}
              onBlur={(e) => commitEdgeLabel(edgeLabelPopup.edgeId, e.target.value)}
            />
            <span style={{ fontSize: 10, color: "#9ca3af" }}>Enter to save</span>
          </div>
        )}

        {/* Canvas */}
        <div
          ref={canvasRef}
          style={{ flex: 1 }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
          onDrop={handleDrop}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeDoubleClick={onEdgeDoubleClick}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode="Delete"
            nodesFocusable={false}
          >
            <Background />
            <Controls />
            <MiniMap />

            <Panel position="bottom-center">
              <div style={{ fontSize: 11, color: "#9ca3af", background: "rgba(255,255,255,0.9)", padding: "4px 10px", borderRadius: 20 }}>
                Drag a node type onto the canvas • Connect nodes by dragging handles • Delete key removes selected
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>
      </div>
    </div>
  );
};

const FlowBuilder = () => (
  <ReactFlowProvider>
    <FlowBuilderInner />
  </ReactFlowProvider>
);

export default FlowBuilder;
